import React, { useState, useEffect } from 'react';
import api from '../lib/api';

const MaintenancePage = ({ onRetry }) => {
  const [retrying, setRetrying] = useState(false);
  const [logoUrl, setLogoUrl] = useState(() => {
    // Try cached logo first (saved by other pages that successfully loaded it)
    try { return localStorage.getItem('app_logo_url') || '/logo-transparent.png'; }
    catch { return '/logo-transparent.png'; }
  });

  // Try to fetch the dynamic logo (backend may still be up even if Supabase is down)
  useEffect(() => {
    api.get('/api/settings/version')
      .then(res => {
        if (res.data?.logo_url) {
          setLogoUrl(res.data.logo_url);
          try { localStorage.setItem('app_logo_url', res.data.logo_url); } catch {}
        }
      })
      .catch(() => {}); // Silently fail — use cached or static logo
  }, []);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      if (onRetry) {
        await onRetry();
      } else {
        window.location.reload();
      }
    } catch {
      // If retry fails, just reset the button
    }
    // Small delay so user sees the spinner
    setTimeout(() => setRetrying(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <img
            src={logoUrl}
            alt="Bohol Trabaho"
            className="h-20 w-20 mx-auto mb-4 object-contain"
            onError={(e) => {
              if (e.target.src !== '/logo-transparent.png') {
                e.target.src = '/logo-transparent.png';
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
          <h1 className="text-2xl font-bold text-gray-900">Bohol Trabaho</h1>
        </div>

        {/* Maintenance icon */}
        <div className="mb-6">
          <div className="w-24 h-24 mx-auto bg-indigo-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-12 h-12 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
            </svg>
          </div>
        </div>

        {/* Message */}
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          We're under maintenance
        </h2>
        <p className="text-gray-500 mb-8 leading-relaxed">
          Our servers are temporarily unavailable. This usually resolves within a few minutes. We apologize for the inconvenience.
        </p>

        {/* Retry button */}
        <button
          onClick={handleRetry}
          disabled={retrying}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-medium transition-all duration-200 disabled:opacity-60"
          style={{
            background: 'linear-gradient(135deg, hsl(238, 76%, 58%), hsl(258, 76%, 58%))',
            boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)'
          }}
        >
          {retrying ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Checking...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
              </svg>
              Try Again
            </>
          )}
        </button>

        {/* Status hint */}
        <p className="mt-8 text-xs text-gray-400">
          If this persists, please check back in a few minutes
        </p>
      </div>
    </div>
  );
};

export default MaintenancePage;
